import React, { useState } from 'react';
import Icon from '../../common/Icon/Icon';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import { adminFetch, uptime, until, clock, STATE_WORD, Dot } from './adminUtil';

/**
 * The first thing an admin sees: is everything running, and what needs me.
 * One tile per system (click it to go where that system lives), then the
 * needs-attention list, most urgent first, each with its fix.
 */

function HealthTile({ icon, name, state, lines, onClick, meter }) {
    return (
        <button type="button" className={`adm-tile ${state}`} onClick={onClick} disabled={!onClick}>
            <span className="adm-tile-top">
                <Icon name={icon} size={15} />
                {name}
                <Dot state={state} />
            </span>
            <span className="adm-tile-word">{STATE_WORD[state] || state}</span>
            {meter != null && (
                <span className="adm-meter" aria-hidden="true"><span style={{ width: `${meter}%` }} /></span>
            )}
            {lines.filter(Boolean).map((l, i) => <span key={i} className="adm-tile-line">{l}</span>)}
        </button>
    );
}

function HealthStrip({ h, go }) {
    const jobs = h.pipeline.jobs;
    const next = [...jobs].sort((a, b) => Date.parse(a.nextAt) - Date.parse(b.nextAt))[0];
    const troubled = jobs.find(j => j.state !== 'ok');
    return (
        <div className="adm-tiles">
            <HealthTile
                icon="server" name="Website" state={h.website.state}
                onClick={() => go('errors')}
                lines={[
                    `Up ${uptime(h.website.uptimeSeconds)} · ${h.website.memoryMb} MB`,
                    h.website.crashes24h
                        ? `${h.website.crashes24h} crash${h.website.crashes24h === 1 ? '' : 'es'} today`
                        : h.website.starts24h > 1 ? `Restarted ${h.website.starts24h - 1}x today` : `Node ${h.website.node}`,
                ]}
            />
            <HealthTile
                icon="database" name="Database" state={h.database.state}
                lines={h.database.state === 'bad'
                    ? ['Not answering', h.database.error]
                    : [`Answers in ${h.database.ms} ms`, `Up ${uptime(h.database.uptimeSeconds)}`]}
            />
            <HealthTile
                icon="clock" name="Nightly jobs" state={h.pipeline.state}
                onClick={() => go('catalog')}
                lines={[
                    troubled ? `${troubled.label}: ${troubled.note}` : 'All ran on schedule',
                    next && `Next: ${next.label.toLowerCase()} ${until(next.nextAt)} (${clock(next.nextAt)})`,
                ]}
            />
            <HealthTile
                icon="disk" name="Disk" state={h.disk.state}
                meter={h.disk.freePct != null ? 100 - h.disk.freePct : null}
                lines={h.disk.freeGb != null
                    ? [`${h.disk.freeGb} GB free of ${h.disk.totalGb} GB`]
                    : ['Could not read the disk']}
            />
            <HealthTile
                icon="mail" name="Email" state={h.email.state}
                onClick={h.email.failures7d ? () => go('errors') : undefined}
                lines={[
                    !h.email.configured ? 'Not set up (no Resend key)'
                        : h.email.failures7d ? `${h.email.failures7d} failed this week` : 'Sending through Resend',
                    h.email.chatNeedsVerifiedEmail ? 'Chat needs a verified email' : 'Chat email check is off',
                ]}
            />
        </div>
    );
}

function AdminOverview({ data, error, reload, go }) {
    const [busy, setBusy] = useState(null);      // item id being acted on
    const [msg, setMsg] = useState(null);
    const [confirm, setConfirm] = useState(null); // { item, action }

    if (!data) {
        return error
            ? <div className="adm-banner bad"><Icon name="alert" size={18} /> {error} The database may be down.</div>
            : <p className="adm-loading">Checking every system&hellip;</p>;
    }

    const { health: h, attention } = data;
    const high = attention.filter(a => a.level === 'high').length;
    const overall = high ? 'bad' : attention.length ? 'warn' : 'ok';

    const run = async (item, action, confirmed = false) => {
        setMsg(null);
        if (action.type === 'tab') return go(action.tab);
        if (action.type === 'lookup') return go('lookup', action.trade ? { trade: action.trade } : { user: action.user });
        if (action.confirm && !confirmed) return setConfirm({ item, action });
        setBusy(item.id);
        try {
            if (action.type === 'job') {
                await adminFetch(`/api/admin/jobs/${action.job}`, { method: 'POST' });
                go('catalog');
                return;
            }
            if (action.type === 'post') {
                await adminFetch(action.url, { method: 'POST', body: action.body || {} });
                await reload();
            }
        } catch (e) {
            setMsg(e.message);
        } finally {
            setBusy(null);
            setConfirm(null);
        }
    };

    return (
        <div className="adm-overview">
            <ConfirmDialog
                open={!!confirm}
                title={confirm?.action.label}
                message={confirm?.action.confirm}
                confirmLabel={confirm?.action.label}
                danger={!!confirm?.action.danger}
                busy={!!busy}
                onCancel={() => setConfirm(null)}
                onConfirm={() => run(confirm.item, confirm.action, true)}
            />

            <section className={`adm-hero ${overall}`}>
                <span className="adm-orb" aria-hidden="true">
                    <Icon name={overall === 'ok' ? 'check' : 'alert'} size={26} />
                </span>
                <div>
                    <h2>
                        {attention.length === 0
                            ? 'All clear'
                            : `${attention.length} ${attention.length === 1 ? 'thing needs' : 'things need'} you`}
                    </h2>
                    <p>
                        {attention.length === 0
                            ? 'Every system is running and nothing is waiting on you.'
                            : high
                                ? `${high} urgent. The list starts with the most urgent.`
                                : 'Nothing urgent. The list starts with the most important.'}
                    </p>
                </div>
            </section>

            {!h.isLive && (
                <p className="adm-devnote">
                    <Icon name="server" size={14} />
                    You're on <strong>{h.host}</strong>, not the live server. Data and the nightly jobs are shared
                    with {h.liveHost}; disk, errors and the Run buttons here are this machine's.
                </p>
            )}

            <HealthStrip h={h} go={go} />

            <section className="adm-attn">
                <h3 className="adm-h3">Needs attention {attention.length > 0 && <span>{attention.length}</span>}</h3>
                {msg && <p className="adm-msg">{msg}</p>}
                {attention.length === 0 ? (
                    <div className="adm-empty">
                        <Icon name="check-circle" size={28} />
                        Nothing needs you right now.
                    </div>
                ) : (
                    <ol className="adm-attn-list">
                        {attention.map(item => (
                            <li key={item.id} className={`adm-attn-item ${item.level}`}>
                                <div className="adm-attn-text">
                                    <span className="adm-attn-area">{item.area}</span>
                                    <strong>{item.title}</strong>
                                    <p>{item.detail}</p>
                                </div>
                                {item.actions.length > 0 && (
                                    <div className="adm-attn-actions">
                                        {item.actions.map((a, i) => (
                                            <button
                                                key={a.label}
                                                type="button"
                                                className={`adm-btn${i === 0 && !a.danger ? ' primary' : ''}${a.danger ? ' danger' : ''}`}
                                                disabled={busy === item.id}
                                                onClick={() => run(item, a)}
                                            >
                                                {a.type === 'job' && <Icon name="play" size={12} />}
                                                {a.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ol>
                )}
            </section>
        </div>
    );
}

export default AdminOverview;
